import { createBrowserRouter, Navigate } from '../compat/router-shim';
import { Layout } from '../components/templates/Layout';
import { routePaths } from './basePaths';
import { PageNotFound } from '../pages/404';
import { NewInspection } from '../pages/NewInspection';
import { homeRoute } from './home.routes';
import { inspectionRoutes } from './inspection.routes';
import { LoginScreen } from '../pages/LoginScreen';
import { NewBlankBucket } from '../pages/inspections/bucket/NewBlankBucket';
import azureAuthService from '../services/azureAuthService';
import { checkNetworkStatus } from '../store/network/networkState';

// Create router lazily to ensure MSAL is initialized first
let routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

const getRouter = () => {
  if (routerInstance) return routerInstance;

  routerInstance = createBrowserRouter([
    {
      path: '/login',
      element: <LoginScreen />,
    },
    {
      path: '/',
      element: <Layout />,
      errorElement: <PageNotFound />,
      HydrateFallback: () => (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mx-auto size-12 animate-spin rounded-full border-b-2 border-brand"></div>
            <p className="mt-4 text-inkMuted">Loading...</p>
          </div>
        </div>
      ),
      loader: async () => {
        console.log(
          '🟠 Route Loader: Checking authentication for path:',
          window.location.pathname,
        );

        // CRITICAL: First check if this is a redirect from Microsoft authentication
        // We need to let MSAL process the redirect BEFORE checking for tokens
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthCode = urlParams.has('code');
        const hasState = urlParams.has('state');
        const msalLoginInProgress = sessionStorage.getItem(
          'msalLoginInProgress',
        );

        if ((hasAuthCode && hasState) || msalLoginInProgress) {
          console.log(
            '🟠 Route Loader: Detected Microsoft auth redirect, allowing MSAL to process',
          );

          // Clear the flag
          sessionStorage.removeItem('msalLoginInProgress');

          // This is a redirect from Microsoft - let MSAL handle it first
          // Initialize MSAL and handle the redirect
          const redirectResponse = await azureAuthService.initialize();

          if (redirectResponse) {
            console.log(
              '🟠 Route Loader: MSAL processed redirect successfully',
            );
            // Set a flag that we just processed a login
            sessionStorage.setItem('hasProcessedLogin', 'true');
            // Token should now be stored, allow access
            return null;
          }
        }

        const isOnline = await checkNetworkStatus();
        if (!isOnline) {
          // Check if we have an existing valid token for offline access
          const jwtToken = localStorage.getItem('jwtToken');
          if (jwtToken) {
            console.warn(
              'Network is offline but token exists, allowing offline access',
            );
            return null;
          }
          console.warn(
            'Network is offline and no token exists, redirecting to login',
          );
          const currentSearch = window.location.search;
          window.location.href = `/login${currentSearch}`;
          return null;
        }

        // Check if we've just redirected from a login (give localStorage time to update)
        const hasProcessedLogin = sessionStorage.getItem('hasProcessedLogin');

        if (hasProcessedLogin) {
          console.log('Recently processed login, allowing route access');
          // Clear the flag after successful navigation
          setTimeout(() => {
            sessionStorage.removeItem('hasProcessedLogin');
          }, 1000);
          return null; // Allow access
        }

        // Check if user is authenticated
        const jwtToken = localStorage.getItem('jwtToken');
        if (!jwtToken) {
          console.log('No JWT token found, redirecting to login');
          const currentSearch = window.location.search;
          // Redirect to login page
          window.location.href = `/login${currentSearch}`;
          return null;
        }

        // If JWT token exists, check that it is valid
        const valid = await azureAuthService.validateAndRefreshToken();
        if (!valid) {
          console.log('JWT token is invalid, redirecting to login');
          const currentSearch = window.location.search;
          // Redirect to login page
          window.location.href = `/login${currentSearch}`;
          return null;
        }

        console.log('JWT token found, allowing route access');
        return null; // Allow access to protected routes
      },
      children: [
        homeRoute,
        {
          path: routePaths.NewInspection,
          element: <NewInspection />,
        },
        {
          // The history screen folded into the front page (its tabs, search and
          // paged list live on Home now). Redirect keeps stale PWA links alive.
          path: '/history',
          element: <Navigate to="/" replace />,
        },
        {
          path: routePaths.Summary,
          lazy: async () => {
            const { SummaryScreen } = await import('../pages/SummaryScreen');
            return {
              element: <SummaryScreen />,
            };
          },
        },
        {
          path: routePaths.TableOfBucket,
          lazy: async () => {
            const { TableOfBucketScreen } =
              await import('../pages/TableOfBucketScreen');
            return {
              element: <TableOfBucketScreen />,
            };
          },
        },
        {
          path: routePaths.HelpScreen,
          lazy: async () => {
            const { HelpScreen } = await import('../pages/help/HelpScreen');
            return {
              element: <HelpScreen />,
            };
          },
          children: [
            {
              path: routePaths.HelpScreen + '/:categoryName',
              lazy: async () => {
                const { HelpScreen } = await import('../pages/help/HelpScreen');
                return {
                  element: <HelpScreen />,
                };
              },
            },
            {
              path: routePaths.HelpScreen + '/:categoryName/:questionId',
              lazy: async () => {
                const { HelpScreen } = await import('../pages/help/HelpScreen');
                return {
                  element: <HelpScreen />,
                };
              },
            },
          ],
        },
        ...inspectionRoutes,
        {
          path: routePaths.NewBucket,
          element: <NewBlankBucket />,
        },
        {
          path: routePaths.SupportScreen,
          lazy: async () => {
            const { SupportScreen } = await import('../pages/SupportScreen');
            return {
              element: <SupportScreen />,
            };
          },
        },
        // you can continue here without worry about layout
      ],
    },
  ]);

  return routerInstance;
};

// Export router getter for use in App
export const router = getRouter;
